<template>
  <div
    v-if="userStore.isImpersonated"
    class="bg-yellow-500 text-black text-center py-2 font-semibold text-sm fixed bottom-0 left-0 w-full z-50 shadow-[0_-2px_8px_rgba(0,0,0,0.15)] flex items-center justify-center gap-3"
  >
    <span class="inline-flex items-center gap-1">
      ⚠️ Impersonating <strong>{{ userStore.email }}</strong>
      <span v-if="userStore.impersonatedBy" class="opacity-75">
        (by {{ userStore.impersonatedBy }})
      </span>
    </span>

    <span
      v-if="countdown !== null"
      class="bg-black/10 rounded px-2 py-0.5 tabular-nums"
      :class="{ 'text-red-800 font-bold animate-pulse': isExpiringSoon }"
    >
      {{ formattedCountdown }}
    </span>

    <button
      class="bg-black/20 hover:bg-black/30 rounded px-3 py-0.5 transition-colors"
      @click="endImpersonation"
    >
      End Session
    </button>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, onUnmounted } from "vue";
import { useRouter } from "vue-router";
import { useUserStore } from "@/stores/user";

/**
 * ImpersonationBanner — fixed yellow bar at the BOTTOM of the page
 * visible only during admin impersonation sessions.
 *
 * Features:
 * - Live countdown timer showing time remaining until token expiry
 * - Pulses red when < 60 seconds remain
 * - Auto-logout when countdown hits zero
 * - "End Session" button for manual exit
 */
export default defineComponent({
  name: "ImpersonationBanner",

  setup() {
    const userStore = useUserStore();
    const router = useRouter();
    const countdown = ref<number | null>(null);
    let intervalId: ReturnType<typeof setInterval> | null = null;

    const isExpiringSoon = computed(() => {
      return countdown.value !== null && countdown.value <= 60;
    });

    const formattedCountdown = computed(() => {
      if (countdown.value === null) return "";
      const total = Math.max(0, countdown.value);
      const m = Math.floor(total / 60);
      const s = total % 60;
      return `${m}:${s.toString().padStart(2, "0")}`;
    });

    function updateCountdown() {
      const expiresAt = userStore.impersonationExpiresAt;
      if (!expiresAt) {
        countdown.value = null;
        return;
      }
      const remaining = expiresAt - Math.floor(Date.now() / 1000);
      countdown.value = Math.max(0, remaining);

      if (remaining <= 0) {
        endImpersonation();
      }
    }

    async function endImpersonation() {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
      // Leave ExpertPrograms before clearStore clears selectedProgram (avoids ProgramsList mounting).
      try {
        await router.push("/");
      } finally {
        userStore.clearStore();
      }
    }

    // Only start/stop the interval when an impersonation session is actually active.
    // This avoids a perpetual 1s timer running on every page load.
    watch(
      () =>
        [userStore.isImpersonated, userStore.impersonationExpiresAt] as const,
      ([isImpersonated, expiresAt]) => {
        if (intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
        if (isImpersonated && expiresAt) {
          updateCountdown();
          intervalId = setInterval(updateCountdown, 1000);
        } else {
          countdown.value = null;
        }
      },
      { immediate: true }
    );

    onUnmounted(() => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    });

    return {
      userStore,
      countdown,
      isExpiringSoon,
      formattedCountdown,
      endImpersonation,
    };
  },
});
</script>
